/**
 * «У вас есть незавершённая заявка».
 *
 * The draft already survives a closed tab — that is what `draftStorage` is for — but until now
 * nothing on the home screen said so. Somebody who filled in four steps and got interrupted came
 * back to a screen offering to start over, and «Заказать заправку» does resume the draft, silently,
 * which is worse than not resuming it: the wizard opens on step four with no explanation of why.
 *
 * **Крестик убирает напоминание НАСОВСЕМ — вместе с черновиком.** Раньше он переключал состояние
 * компонента, черновик оставался в хранилище, и напоминание возвращалось на следующем заходе на
 * главную — а уйти на «Историю» и вернуться достаточно, потому что вкладки здесь настоящие
 * маршруты и главная размонтируется. Человек нажал «убрать» и получил то же самое обратно:
 * «спрятать на время» он не просил и понять это по крестику не мог. Убрать нечем было вовсе —
 * TTL в сутки не наступал, потому что каждый заход в мастер переписывал `savedAt`.
 *
 * Раз действие необратимое — перед ним вопрос. Ровно одна фраза про последствие: адрес, комментарий
 * и объём человек вводил руками, и восстановить их будет неоткуда.
 *
 * Карточек неоплаченных платежей СБП это не касается: у них крестика нет и не будет — за ними
 * деньги, и спрятать такую карточку значит потерять из виду оплату, по которой ещё оформляется
 * заказ или готовится возврат.
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { clearOrderDraft } from '@/entities/order/draftStorage';
import type { OrderDraft } from '@/entities/order/model/draft';
import { Button } from '@/shared/ui/Button';
import { ConfirmSheet } from '@/shared/ui/ConfirmSheet';
import { CloseIcon } from '@/shared/ui/icons';

export function ResumeDraftCard({ draft }: { draft: OrderDraft }) {
  const navigate = useNavigate();
  const [asking, setAsking] = useState(false);
  const [removed, setRemoved] = useState(false);

  if (removed) return null;

  return (
    <section className="flex flex-col gap-2.5 rounded-md bg-info-soft px-3.5 py-3">
      <div className="flex items-start gap-2.5">
        <div className="min-w-0 flex-1">
          <strong className="block text-[13px] font-bold leading-[1.3]">
            У вас есть незавершённая заявка
          </strong>
          <span className="block text-[13px] leading-[1.3] text-ink-soft">
            {draftSummary(draft)}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setAsking(true)}
          // «Убрать», а не «скрыть»: имя действия должно совпадать с тем, что оно делает.
          aria-label="Убрать незавершённую заявку"
          // Крестик нарисован мелким, но нажимается площадкой 44×44 (замер 12.09.2026 дал 20×20).
          // Цена промаха здесь выше обычной: рядом «Продолжить», а этот крестик удаляет черновик.
          className="grid h-11 w-11 shrink-0 place-items-center rounded-sm text-ink-soft"
        >
          <CloseIcon size={12} />
        </button>
      </div>

      <Button variant="secondary" onClick={() => void navigate({ to: '/order/new' })}>
        Продолжить
      </Button>

      <ConfirmSheet
        open={asking}
        title="Убрать незавершённую заявку?"
        subtitle="Введённые данные не сохранятся."
        confirmLabel="Убрать"
        onConfirm={() => {
          clearOrderDraft();
          setAsking(false);
          setRemoved(true);
        }}
        onClose={() => setAsking(false)}
      />
    </section>
  );
}

/**
 * What the draft is about, in one line.
 *
 * The address if there is one, because that is what identifies an order to the person who started
 * it. Without one the draft has barely begun, and the generic question is the honest caption —
 * naming the car instead would imply more progress than exists.
 */
function draftSummary(draft: OrderDraft): string {
  const address = draft.addressLabel?.trim();
  if (address !== undefined && address.length > 0) return address;
  return 'Продолжить оформление?';
}
